import React, { useState } from 'react';
import { Platform, TouchableOpacity, View } from 'react-native';
import type { StyleProp, ViewStyle } from 'react-native';
import { BlurredExpoImage } from '@/components/ui/BlurredExpoImage';
import { LOCKED_CONTENT_BLUR } from '@/constants/lockedContentBlur';
import { Member } from '@/models/Member';
import { Link } from 'expo-router';
import { HugeIconsWrapper } from '~/components/ui/HugeIconsWrapper';
import { getMemberPath } from '@/constants/routes';
import { SquareLock02Icon } from '@hugeicons-pro/core-solid-rounded';
import { MemberLockedDialog } from '@/components/domain/members/MemberLockedDialog';
import { ProfileTooltip } from '~/components/domain/profile/ProfileTooltip';
import { ProtectedImageContainer } from '@/components/ui/ProtectedImageContainer';
interface ProfilePictureProps {
  member: Member;
  showPresence?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'custom';
  className?: string;
  imageClassName?: string;
  indicatorClassName?: string;
  style?: StyleProp<ViewStyle>;
  disableNavigation?: boolean;
  showTooltip?: boolean;
}

const sizeConfig = {
  xs: {
    container: 'h-8 w-8',
    indicator: 'h-3 w-3',
    border: 'border',
    position: 'bottom-0 right-0',
    lockIcon: 16,
  },
  sm: {
    container: 'h-10 w-10',
    indicator: 'h-3 w-3',
    border: 'border',
    position: 'bottom-0.5 right-0.5',
    lockIcon: 18,
  },
  md: {
    container: 'h-12 w-12',
    indicator: 'h-4 w-4',
    border: 'border-2',
    position: 'bottom-0 right-0',
    lockIcon: 20,
  },
  lg: {
    container: 'h-16 w-16',
    indicator: 'h-5 w-5',
    border: 'border-2',
    position: 'bottom-0.5 right-0.5',
    lockIcon: 24,
  },
  xl: {
    container: 'h-20 w-20',
    indicator: 'h-6 w-6',
    border: 'border-3',
    position: 'bottom-1 right-1',
    lockIcon: 32,
  },
};

function ProfilePictureComponent({
  member,
  showPresence = true,
  size = 'md',
  className = '',
  imageClassName = '',
  indicatorClassName = '',
  style,
  disableNavigation = false,
  showTooltip = false,
}: ProfilePictureProps) {
  const [showLockedDialog, setShowLockedDialog] = useState(false);

  const showPresenceIndicator = showPresence && !member.IsLocked && (member.IsOnline || member.IsAway);
  const presenceColorClass = member.IsOnline
    ? 'bg-green-500'
    : member.IsAway
      ? 'bg-yellow-500'
      : '';

  // Handle custom size - don't apply predefined sizing
  const isCustomSize = size === 'custom';
  const { container, indicator, border, position, lockIcon } = isCustomSize 
    ? { container: '', indicator: 'h-6 w-6', border: 'border-3', position: 'bottom-1 right-1', lockIcon: 32 }
    : sizeConfig[size];

  const handleLockedPress = () => {
    setShowLockedDialog(true);
  };

  const renderContent = () => (
    <ProtectedImageContainer className="h-full w-full">
      <BlurredExpoImage
        source={{ uri: member.IconUrl }}
        className={`h-full w-full rounded-full ${imageClassName}`}
        contentFit="cover"
        recyclingKey={member.IconUrl}
        blur={member.IsLocked ? LOCKED_CONTENT_BLUR : undefined}
      />

      {member.IsLocked && (
        <View className="absolute inset-0 items-center justify-center rounded-full bg-black/30" pointerEvents="none">
          <HugeIconsWrapper
            icon={SquareLock02Icon}
            size={lockIcon}
            className="text-white/70"
          />
        </View>
      )}

      {showPresenceIndicator && (
        <>
          {/* Use nested views for more consistent cross-platform border rendering */}
          <View
            className={`absolute -mb-0.5 -mr-0.5 ${position} ${indicator} rounded-full ${border} border-background-0 ${indicatorClassName}`}>
            <View className={`h-full w-full rounded-full ${presenceColorClass}`} />
          </View>
        </>
      )}
    </ProtectedImageContainer>
  );

  // `focusable={false}` (RNW maps to tabIndex="-1") keeps the avatar out of the
  // keyboard tab order. Inside a focus-trapping drawer (Gluestack Modal/Drawer
  // wraps content in React Aria's <FocusScope contain>), when a focused button
  // becomes disabled mid-action (e.g. comments reload/submit) the trap calls
  // focusFirstInScope and would land on the first comment's avatar, firing the
  // tooltip's onFocus and popping it open. Skipping avatars is safe because
  // every caller renders an adjacent username link to the same profile.
  return (
    <ProfileTooltip member={member} isActive={showTooltip && !member.IsLocked}>
      {disableNavigation ? (
        <View className={`relative bg-background-100 rounded-full ${container} ${className}`} style={style}>
          {renderContent()}
        </View>
      ) : member.IsLocked ? (
        <>
          <TouchableOpacity
            activeOpacity={0.8}
            onPress={handleLockedPress}
            focusable={false}
            className={`relative bg-background-100 rounded-full ${container} ${className}`}
            style={style}
          >
            {renderContent()}
          </TouchableOpacity>
          <MemberLockedDialog
            isOpen={showLockedDialog}
            onClose={() => setShowLockedDialog(false)}
          />
        </>
      ) : (
        <Link href={getMemberPath(member)} asChild>
          <TouchableOpacity
            activeOpacity={0.8}
            focusable={false}
            className={`relative bg-background-100 rounded-full ${container} ${className}`}
            style={style}
          >
            {renderContent()}
          </TouchableOpacity>
        </Link>
      )}
    </ProfileTooltip>
  );
}

// Rendered in every post/message/comment/notification row; memo keeps parent
// list re-renders from re-diffing each avatar subtree.
export const ProfilePicture = React.memo(ProfilePictureComponent);